/**
 * FileUploadSection.js
 * State-of-the-art file upload interface with modern design and mobile-first approach
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Fade,
  Zoom,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccountBalance as BankIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useQueryBanks } from '../../services/api/banksService';
import { useSelector } from 'react-redux';

const FileUploadSection = ({ onFileUpload, isUploading, uploadProgress }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get company context
  const companyId =
    useSelector((state) => state.auth?.companyId) || localStorage.getItem('companyId');

  // Fetch available bank accounts
  const { data: banksData, isLoading: isLoadingBanks } = useQueryBanks(companyId, {
    enabled: !!companyId,
  });
  const banks = banksData?.data || [];

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadStatus('error');
      return;
    }

    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setUploadStatus(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/x-ofx': ['.ofx'],
      'application/octet-stream': ['.ofx'],
      'text/plain': ['.ofx'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isUploading,
  });

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !selectedBankAccountId) {
      setUploadStatus('error');
      return;
    }

    try {
      setUploadStatus(null);
      await onFileUpload(selectedFile, selectedBankAccountId);
      setUploadStatus('success');

      // Clear form after successful upload
      setTimeout(() => {
        setSelectedFile(null);
        setSelectedBankAccountId('');
        setUploadStatus(null);
      }, 3000);
    } catch (error) {
      setUploadStatus('error');
    }
  }, [selectedFile, selectedBankAccountId, onFileUpload]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setUploadStatus(null);
  }, []);

  const getDropzoneStyles = () => {
    const baseStyles = {
      p: { xs: 3, sm: 4 },
      border: '2px dashed',
      borderRadius: 3,
      textAlign: 'center',
      cursor: isUploading ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.8
      )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
      backdropFilter: 'blur(10px)',
      minHeight: { xs: 160, sm: 180 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.02
        )} 100%)`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
    };

    if (isDragReject) {
      return {
        ...baseStyles,
        borderColor: theme.palette.error.main,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(
          theme.palette.error.light,
          0.04
        )} 100%)`,
        transform: 'scale(1.02)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.error.main, 0.2)}`,
        '&::before': { opacity: 1 },
      };
    }

    if (isDragActive) {
      return {
        ...baseStyles,
        borderColor: theme.palette.primary.main,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
          theme.palette.primary.light,
          0.04
        )} 100%)`,
        transform: 'scale(1.02)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
        '&::before': { opacity: 1 },
      };
    }

    if (selectedFile) {
      return {
        ...baseStyles,
        borderColor: theme.palette.success.main,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(
          theme.palette.success.light,
          0.04
        )} 100%)`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.2)}`,
        '&::before': { opacity: 1 },
      };
    }

    return {
      ...baseStyles,
      borderColor: alpha(theme.palette.primary.main, 0.3),
      '&:hover': {
        borderColor: theme.palette.primary.main,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
          theme.palette.primary.light,
          0.03
        )} 100%)`,
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
        '&::before': { opacity: 1 },
      },
    };
  };

  const renderDropzoneContent = () => {
    if (isUploading) {
      return (
        <Fade in timeout={500}>
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <Zoom in timeout={600}>
              <Box sx={{ mb: 2 }}>
                <UploadIcon
                  sx={{
                    fontSize: { xs: 48, sm: 56 },
                    color: theme.palette.primary.main,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.1)', opacity: 0.7 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                />
              </Box>
            </Zoom>
            <Typography variant="h6" fontWeight="600" color="primary" gutterBottom>
              Processando arquivo...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {uploadProgress}% concluído
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 300, mx: 'auto' }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  },
                }}
              />
            </Box>
          </Box>
        </Fade>
      );
    }

    if (selectedFile) {
      return (
        <Fade in timeout={500}>
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <Zoom in timeout={600}>
              <Box sx={{ mb: 2 }}>
                <CheckCircleIcon
                  sx={{
                    fontSize: { xs: 48, sm: 56 },
                    color: theme.palette.success.main,
                  }}
                />
              </Box>
            </Zoom>
            <Typography variant="h6" fontWeight="600" color="success.main" gutterBottom>
              Arquivo selecionado
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
                flexWrap: 'wrap',
              }}
            >
              <DocumentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight="500" sx={{ wordBreak: 'break-all' }}>
                {selectedFile.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              sx={{
                mt: 1,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Fade>
      );
    }

    return (
      <Fade in timeout={500}>
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Zoom in timeout={600}>
            <Box sx={{ mb: 2 }}>
              <AttachFileIcon
                sx={{
                  fontSize: { xs: 48, sm: 56 },
                  color: alpha(theme.palette.primary.main, 0.6),
                }}
              />
            </Box>
          </Zoom>
          <Typography
            variant="h6"
            fontWeight="600"
            color="text.primary"
            gutterBottom
            sx={{ mb: 1 }}
          >
            {isDragActive ? 'Solte o arquivo aqui' : 'Upload de Extrato Bancário'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
            Arraste e solte seu arquivo OFX ou clique para selecionar
          </Typography>
          <Chip
            label=".ofx"
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 500,
            }}
          />
        </Box>
      </Fade>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Modern Dropzone Card */}
      <Card {...getRootProps()} sx={getDropzoneStyles()} elevation={0}>
        <input {...getInputProps()} />
        {renderDropzoneContent()}
      </Card>

      {/* Enhanced Account Selection and Upload Controls */}
      {selectedFile && (
        <Fade in timeout={300}>
          <Card
            sx={{
              mt: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.background.paper,
                0.9
              )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
            elevation={0}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight="600"
                color="text.primary"
                gutterBottom
                sx={{ mb: 3 }}
              >
                Configurar Upload
              </Typography>

              <Stack spacing={3}>
                {/* Bank Account Selection */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    color="text.secondary"
                    sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <BankIcon sx={{ fontSize: 18 }} />
                    Selecione a conta bancária
                  </Typography>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Conta Bancária</InputLabel>
                    <Select
                      value={selectedBankAccountId}
                      onChange={(e) => setSelectedBankAccountId(e.target.value)}
                      label="Conta Bancária"
                      disabled={isUploading || isLoadingBanks}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: 2,
                        },
                      }}
                    >
                      {banks.map((bank) => (
                        <MenuItem key={bank.id} value={bank.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <BankIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="500">
                                {bank.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                •••• {bank.accountBankNumber?.slice(-4)}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Upload Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size={isMobile ? 'medium' : 'large'}
                    onClick={handleUpload}
                    disabled={!selectedBankAccountId || isUploading}
                    sx={{
                      minWidth: { xs: 120, sm: 140 },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1, sm: 1.5 },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        background: 'grey.300',
                        boxShadow: 'none',
                        transform: 'none',
                      },
                    }}
                  >
                    {isUploading ? 'Processando...' : 'Fazer Upload'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Enhanced Status Messages */}
      {uploadStatus === 'success' && (
        <Fade in timeout={300}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              mt: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.1
              )} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              '& .MuiAlert-icon': {
                color: theme.palette.success.main,
              },
            }}
          >
            <Typography variant="body2" fontWeight="500">
              Extrato processado com sucesso!
            </Typography>
            <Typography variant="caption" color="success.dark">
              O arquivo foi enviado e está sendo analisado pelo sistema.
            </Typography>
          </Alert>
        </Fade>
      )}

      {uploadStatus === 'error' && (
        <Fade in timeout={300}>
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            sx={{
              mt: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.error.main,
                0.1
              )} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              '& .MuiAlert-icon': {
                color: theme.palette.error.main,
              },
            }}
          >
            <Typography variant="body2" fontWeight="500">
              Erro ao processar arquivo
            </Typography>
            <Typography variant="caption" color="error.dark">
              Verifique se o arquivo é válido e tente novamente.
            </Typography>
          </Alert>
        </Fade>
      )}
    </Box>
  );
};

export default FileUploadSection;
